import type { DashboardRecord, WidgetInstance } from "./types";
import { DATA_REGISTRY } from "./DataRegistry";
import { getWidget } from "./WidgetRegistry";
import { reportTenantState } from "./reportScope";
import { storedDashboardTenantIds } from "./scope";

type ViewerControl = "tenants" | "time-range" | "collapse-all" | "events";
export type DataControl = "tenants" | "time-range";

// Most live resolvers accept both filters. These sources only accept one; report
// sources have their own fixed scope and period and accept neither.
const PARTIAL_LIVE_CONTROLS: Record<string, readonly DataControl[]> = {
  "event-evolution": ["time-range"],
  "event-scope": ["time-range"],
  "campaign-schedule": ["tenants"],
  "engines-health": ["tenants"],
};

export function widgetViewerControls(widget: WidgetInstance): readonly DataControl[] {
  const sources = Object.entries(getWidget(widget.widgetId)?.manifest.dataRequests ?? {}).map(
    ([key, request]) => widget.dataBindings?.[key]?.source ?? request.source,
  );
  return (["tenants", "time-range"] as const).filter((control) =>
    sources.some((source) => {
      const resolver = DATA_REGISTRY[source];
      return (
        resolver &&
        (resolver.report
          ? control === "time-range"
            ? resolver.report.followScope === true
            : resolver.report.paramKeys.includes("tenant_ids")
          : (PARTIAL_LIVE_CONTROLS[source] ?? ["tenants", "time-range"]).includes(control))
      );
    }),
  );
}

export function viewerControlCoverage(
  widgets: readonly WidgetInstance[],
  enabled: readonly ViewerControl[],
) {
  return (["tenants", "time-range"] as const)
    .filter((control) => enabled.includes(control))
    .map((control) => {
      const affected = widgets.filter((widget) => widgetViewerControls(widget).includes(control));
      const unaffected = widgets.filter(
        (widget) => !widgetViewerControls(widget).includes(control),
      );
      return { control, affected, unaffected };
    })
    .filter(({ affected, unaffected }) => affected.length > 0 && unaffected.length > 0);
}

export function supportedViewerControls(
  widgets: readonly WidgetInstance[],
): readonly DataControl[] {
  if (widgets.length === 0) return ["tenants", "time-range"];
  return (["tenants", "time-range"] as const).filter((control) =>
    widgets.some((widget) => widgetViewerControls(widget).includes(control)),
  );
}

/**
 * How many tenants the dashboard itself covers: its stored tenant subset, else
 * the tenants its report widgets agree on. `null` when it follows the viewer.
 */
export function dashboardTenantCoverage(
  config: DashboardRecord["config"],
  widgets: readonly WidgetInstance[],
): number | null {
  const stored = storedDashboardTenantIds(config.scope);
  if (stored) return stored.length;
  const reports = reportTenantState(widgets);
  return reports.widgets > 0 && !reports.mixed ? reports.ids.length : null;
}

export function effectiveViewerControls(
  config: DashboardRecord["config"],
  widgets: readonly WidgetInstance[],
): ViewerControl[] {
  const supported = supportedViewerControls(widgets);
  // A dashboard about one tenant has nothing to choose: its name is shown under the title.
  const singleTenant = dashboardTenantCoverage(config, widgets) === 1;
  const controls: ViewerControl[] = [
    ...new Set<ViewerControl>(config.filters ?? ["tenants", "time-range"]),
  ];
  return controls.filter((control) => {
    // Neither is driven by a per-widget data source the way tenants/time-range
    // are (no widget declares "accepts an event scope" yet, any more than one
    // declares "is collapsible"): a dashboard's author turns it on directly.
    if (control === "collapse-all" || control === "events") return true;
    return (
      supported.includes(control) &&
      (control !== "tenants" || (config.scope?.tenants !== "all" && !singleTenant))
    );
  });
}
